<template>
  <div class="flexipass-doors">
    <b-card no-body>
      <b-card-header class="pb-50">
        <b-row no-gutters class="w-100 align-items-center">
          <b-col>
            <h4 class="mb-0"><i class="fas fa-door-open mr-50"/>{{ msg('Flexipass doors') }}</h4>
            <small class="text-muted">{{ msg('Locks fetched live from Flexipass for the selected building') }}</small>
          </b-col>
          <b-col cols="auto">
            <b-link style="color: inherit" @click="loadDoors" :disabled="!buildingId">
              <feather-icon icon="RefreshCcwIcon" size="16"/>
            </b-link>
          </b-col>
        </b-row>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="6" lg="4">
            <b-form-group :label="msg('Building')">
              <v-select
                  v-model="buildingId"
                  :options="buildingOptions"
                  :reduce="option => option.value"
                  label="text"
                  :clearable="false"
                  append-to-body
                  @input="loadDoors"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-alert :show="error != null" variant="warning" class="p-1">
          <i class="fas fa-exclamation-triangle mr-50"/>{{ error }}
        </b-alert>

        <div v-if="loading" class="text-center py-3">
          <b-spinner variant="primary"/>
        </div>

        <template v-else-if="doors.length">
          <table class="table b-table table-hover">
            <thead>
            <tr>
              <th>{{ msg('name') }}</th>
              <th class="d-none d-md-table-cell">{{ msg('description') }}</th>
              <th class="d-none d-lg-table-cell">ID</th>
              <th>{{ msg('status') }}</th>
              <th style="min-width: 200px">
                {{ msg('Room') }}
                <info-hint :text="msg('doorroomhint')"/>
              </th>
              <th>
                {{ msg('Common door') }}
                <info-hint :text="msg('commondoorhint')"/>
              </th>
              <th class="text-right">{{ msg('Test') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="door in doors" :key="door.id">
              <td class="font-weight-bold">{{ door.name }}</td>
              <td class="d-none d-md-table-cell text-muted">{{ door.description }}</td>
              <td class="d-none d-lg-table-cell"><code>{{ door.id }}</code></td>
              <td>
                <b-badge :variant="door.online === false ? 'light-danger' : 'light-success'">
                  {{ door.online === false ? msg('Offline') : msg('Online') }}
                </b-badge>
              </td>
              <td>
                <!-- explicit door-to-room link; saves on change like the switch -->
                <v-select
                    :value="doorRooms[door.id] || null"
                    :options="roomOptions"
                    :reduce="option => option.value"
                    label="text"
                    :placeholder="msg('Not linked')"
                    :disabled="savingDoorId !== null"
                    :loading="savingDoorId === door.id"
                    append-to-body
                    @input="value => linkRoom(door, value)"
                />
              </td>
              <td>
                <!-- saves on flip — a separate Save button was one more thing
                     to forget, and a forgotten one silently kept old doors -->
                <b-form-checkbox v-model="commonDoors" :value="door.id" switch
                                 :disabled="saving" @change="saveCommon"/>
              </td>
              <td class="text-right" style="min-width: 220px">
                <!-- the response lands right beside the button — this row is
                     the screenshot Flexipass asks for -->
                <span v-if="openResults[door.id]" class="mr-50"
                      :class="openResults[door.id].ok ? 'text-success' : 'text-danger'">
                  <i :class="openResults[door.id].ok ? 'fas fa-check-circle' : 'fas fa-times-circle'" class="mr-25"/>
                  {{ openResults[door.id].message }}
                </span>
                <b-button size="sm" variant="outline-primary"
                          :disabled="openingDoorId !== null || door.online === false"
                          :title="door.online === false ? msg('Offline') : ''"
                          @click="openDoor(door)">
                  <b-spinner v-if="openingDoorId === door.id" small class="mr-25"/>
                  <i v-else class="fas fa-door-open mr-25"/>{{ msg('open') }}
                </b-button>
              </td>
            </tr>
            </tbody>
          </table>

          <small class="text-muted d-block mt-1">
            <b-spinner v-if="saving" small class="mr-25"/>
            {{ msg('Room doors are matched to reservations by name automatically — only mark entrance doors as common') }}
          </small>
        </template>

        <div v-else-if="buildingId && !error" class="text-center text-muted py-3">
          {{ msg('No doors were returned for this building') }}
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import InfoHint from '@/components/panels/journey/InfoHint.vue'
import api from '@/services/api.service'

/**
 * Staff view of the Flexipass smart locks, per BUILDING: the door list comes
 * live from the Flexipass API (each building has its own token), and the only
 * thing edited here is which doors are "common" — the entrance doors every
 * guest of the building may open. Room doors are matched to reservations by
 * name automatically.
 */
export default {
  name: 'flexipassDoors',
  components: { InfoHint },
  data() {
    return {
      buildings: [],
      buildingId: null,
      doors: [],
      commonDoors: [],
      loading: false,
      saving: false,
      error: null,
      openingDoorId: null,
      // doorId -> {ok, message}; kept per door so several can be tested in a row
      openResults: {},
      // rooms of every building (RealEstateBasic), filtered per selection
      rooms: [],
      // doorId -> realEstateId, mirrored from the server on every load/save
      doorRooms: {},
      savingDoorId: null,
    }
  },
  watch: {
    // on a hard refresh created() can run before the branch selection has
    // loaded — the buildings request filters by it and returns nothing. Reload
    // whenever the selection arrives or changes.
    getSelectedBranchesIds() {
      this.loadBuildings()
    }
  },
  computed: {
    ...mapGetters('data', ['getSelectedBranchesIds']),
    roomOptions() {
      return this.rooms
          .filter(room => room.buildingId === this.buildingId)
          .map(room => ({ value: room.id, text: room.name || room.address || room.id }))
    },
    buildingOptions() {
      // buildings are identified the way buildings.vue shows them: company/name + address
      return this.buildings.map(b => ({
        value: b.id,
        text: [b.name || b.company, b.address, b.location].filter(Boolean).join(', ') || b.id,
      }))
    }
  },
  created() {
    this.loadBuildings()
  },
  methods: {
    ...mapActions('building', ['getAllBuildings', 'getAllBuildingsBasics']),
    loadBuildings() {
      // same call and same response reading as buildings.vue — the proven path
      this.error = null
      this.getAllBuildings({
        page: 1,
        amount: 500,
        filter: {},
        sortcolumn: 'address',
        sortby: 'asc',
      })
          .then(ans => {
            this.buildings = (ans && ans['Buildings']) || []
            if (!this.buildings.length)
              this.error = this.msg('No buildings found — check that a branch is selected in the top bar')
          })
          .catch(e => {
            this.error = (typeof e === 'object' && e.message) ? e.message : String(e)
          })
      // rooms for the door-to-room links (RealEstateBasic carries buildingId)
      this.getAllBuildingsBasics({ properties: true })
          .then(ans => {
            this.rooms = (ans && ans.realEstates) || []
          })
          .catch(() => {})
    },
    loadDoors() {
      if (!this.buildingId) return
      this.loading = true
      this.error = null
      this.doors = []
      this.openResults = {}
      api.sendRequest('flexipass/doors/' + this.buildingId)
          .then(ans => {
            this.doors = ans['Doors'] || []
            // served by the same endpoint so the pre-checked switches never
            // depend on which building DTO shape the list above came from
            this.commonDoors = ans['CommonDoors'] || []
            this.doorRooms = ans['DoorRooms'] || {}
          })
          .catch(e => {
            this.error = (typeof e === 'object' && e.message) ? e.message : String(e)
          })
          .finally(() => {
            this.loading = false
          })
    },
    linkRoom(door, realEstateId) {
      this.savingDoorId = door.id
      api.sendRequest('flexipass/doors/' + this.buildingId + '/room', null,
          { doorId: door.id, realEstateId: realEstateId || null })
          .then(ans => {
            // the server owns the mapping — mirror it, including the case where
            // linking here unlinked the same room from another door
            this.doorRooms = ans['DoorRooms'] || {}
            this.$store.dispatch('notifications/success',
                { title: this.msg('Flexipass doors'), message: this.msg('Door link saved') })
          })
          .catch(e => {
            this.loadDoors()
            this.$store.dispatch('notifications/error',
                { title: this.msg('Flexipass doors'), message: (typeof e === 'object' && e.message) ? e.message : String(e) })
          })
          .finally(() => {
            this.savingDoorId = null
          })
    },
    openDoor(door) {
      this.openingDoorId = door.id
      this.$set(this.openResults, door.id, null)
      api.sendRequest('flexipass/doors/' + this.buildingId + '/open/' + door.id, null, {})
          .then(() => {
            this.$set(this.openResults, door.id, { ok: true, message: this.msg('Door opened') })
          })
          .catch(e => {
            const message = (typeof e === 'object' && e.message) ? e.message : String(e)
            this.$set(this.openResults, door.id, { ok: false, message })
          })
          .finally(() => {
            this.openingDoorId = null
          })
    },
    saveCommon() {
      this.saving = true
      const attempted = [...this.commonDoors]
      api.sendRequest('flexipass/doors/' + this.buildingId + '/common', null, { doorIds: attempted })
          .then(ans => {
            // the server answers with what it stored — mirror it, and revert
            // visibly if the save failed so the switches never lie
            this.commonDoors = ans['CommonDoors'] || attempted
            this.$store.dispatch('notifications/success',
                { title: this.msg('Flexipass doors'), message: this.msg('Common doors saved') })
          })
          .catch(e => {
            this.loadDoors() // reload the stored truth
            this.$store.dispatch('notifications/error',
                { title: this.msg('Flexipass doors'), message: (typeof e === 'object' && e.message) ? e.message : String(e) })
          })
          .finally(() => {
            this.saving = false
          })
    }
  }
}
</script>

<style lang="scss" scoped>
.flexipass-doors {
  code {
    font-size: .8em;
  }
}
</style>
